// Live workflow-activity context — kept separate from the provider component
// so the component file exports only components (react-refresh constraint),
// mirroring auth.ts / AuthProvider.tsx.

import { createContext, useContext } from 'react';
import type { AiGenerationProgressEvent, WorkflowActivityEvent } from '@shared/api-types';

export interface ActivityContextValue {
  /** Whether the SignalR connection is currently up. */
  connected: boolean;
  /**
   * Register a handler for live activity events. Returns an unsubscribe fn —
   * call it on unmount so a page only reacts while it's mounted.
   */
  onWorkflowActivity: (handler: (e: WorkflowActivityEvent) => void) => () => void;
  /**
   * Register a handler for live "AI Generate" progress nudges. Returns an
   * unsubscribe fn. Events are tenant-wide, so filter by `generationId` to track
   * one generation. Same connection as workflow activity (no extra subscribe).
   */
  onAiGenerationProgress: (handler: (e: AiGenerationProgressEvent) => void) => () => void;
  /** Join a workflow's per-workflow group (detail pages). Server verifies ownership. */
  subscribeWorkflow: (workflowId: string) => Promise<void>;
  /** Leave a workflow's per-workflow group. */
  unsubscribeWorkflow: (workflowId: string) => Promise<void>;
}

export const ActivityContext = createContext<ActivityContextValue | null>(null);

/**
 * Access the live-activity connection. Returns null when used outside the
 * provider (e.g. an unauthenticated route) so callers can no-op gracefully.
 */
export function useActivity(): ActivityContextValue | null {
  return useContext(ActivityContext);
}
